import { ObjectUtils } from '@mm/utils';
import _ from 'lodash';
import { Dispatch } from 'redux';
import EntryController from '../controllers/EntryController';
import commonHandler from '../errorHandlers/common-handler';
import { update as updateEntryHandler } from '../errorHandlers/entry-handler';
import WidgetModelHelpers from '../helpers/widget-model-helpers';
import { FormEntry } from '../model/FormsModel';
import { ListAndGalleryEntry } from '../model/WidgetModel';
import { StoreState } from '../store-interfaces';
import EntryChangeManager from '../syncEngine/services/changeManagers/EntryChangeManager';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';
import WidgetCRUDActions from './WidgetCRUDActions';

export type Entry = FormEntry | ListAndGalleryEntry;

class EntryCRUDActions extends CrudActionContainer {
  constructor() {
    super('ENTRY');
  }

  create<T extends Partial<Entry>>(widgetId: number, entry: T) {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      return EntryController.post(widgetId, entry)
        .then(response => {
          dispatch(this.retrievedEntry(widgetId, response));
          dispatch(WidgetCRUDActions.show(widgetId));

          return response as T;
        })
        .catch(commonHandler(dispatch));
    };
  }

  directUpdate<T extends Partial<Entry>>(widgetId: number, entry: T) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));
      try {
        const response = await EntryController.put(widgetId, entry);

        dispatch(this.retrievedEntry(widgetId, response));

        return response;
      } catch (err) {
        await commonHandler(dispatch)(err);
      }
    };
  }

  update<T extends Entry>(
    widgetId: number,
    entry: T,
    withPatch: boolean = true,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const {
        widget: { entities: widgets },
      } = getState();

      const widgetModel = widgets.find(widget => widget.id === widgetId);
      if (widgetModel) {
        const prevEntry = WidgetModelHelpers.getEntry(widgetModel, entry.id);

        dispatch(this.busy(this.updateAction));
        dispatch(WidgetCRUDActions.setBusyWidget(widgetId));
        const entryWithWidgetId = { ...entry, widgetId };
        dispatch(this.updateLocal(entryWithWidgetId));

        const copiedEntry = _.cloneDeep(entry);
        ObjectUtils.removeProperty('file', copiedEntry);
        const promise: Promise<
          T & {
            errors: string[];
          }
        > = EntryChangeManager.update(prevEntry, copiedEntry, widgetId).catch(
          updateEntryHandler(),
        );

        try {
          const { errors, ...response } = await promise;

          if (withPatch) {
            if (errors) {
              const updated = _.cloneDeep(widgetModel);
              const newErrors = errors.map((error: string) => ({
                field: '#/config/name',
                rule: {
                  type: 'Unique',
                  arguments: error.split(':')[1],
                },
              }));

              updated.validationMessages = [
                ...(updated.validationMessages || []),
                ...newErrors,
              ];
              updated.validated.facebook = false;

              dispatch(WidgetCRUDActions.updateLocal(updated));
            } else {
              dispatch(this.updateLocal({ ...response, widgetId }));
              dispatch(WidgetCRUDActions.show(widgetId));
            }
          }
          return response as unknown as T;
        } catch (e) {
          commonHandler(dispatch)(e);
        }
      }
    };
  }

  delete<T extends Partial<Entry>>(
    widgetId: number,
    entry: T,
    withPatch: boolean = true,
  ) {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.updateAction));
      const entryWithWidgetId = { ...entry, widgetId };
      dispatch(this.deleteLocal(entryWithWidgetId));

      let promise: Promise<T> = EntryChangeManager.delete(entry, widgetId);

      if (withPatch) {
        promise = promise.then(response => {
          dispatch(WidgetCRUDActions.show(widgetId));
          return response;
        });
      }

      return promise.catch(commonHandler(dispatch));
    };
  }

  retrievedEntry = <T extends Entry>(widgetId: number, entry: T) => ({
    type: this.retrievedAction,
    payload: { widgetId, entry },
  });
}

export const entryCRUDActions = new EntryCRUDActions();

export const useEntryCRUDActions = getActionHookBuilder(entryCRUDActions, [
  'create',
  'update',
  'delete',
  'retrieved',
  'directUpdate',
]);
